import { Icon } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";
import type { ReactNode } from "react";

import { CARD_ACTIONS_REVEAL_CLASSES } from "./constants";

interface ConversationCardProps {
  actions?: ReactNode;
  dateLabel?: string;
  onClick: () => void;
  preview?: string | null;
  title: string | null;
  variant?: "card" | "list";
}

interface ConversationSummaryProps {
  preview?: string | null;
  title: string | null;
}

const ConversationSummary = ({ preview, title }: ConversationSummaryProps) => (
  <>
    {title && (
      <Typography
        variant="label-sm"
        color="primary"
        truncate
        component="p"
        data-testid="circle-ai-conversation-card-title"
      >
        {title}
      </Typography>
    )}
    {preview && (
      <Typography variant="body-sm" color="tertiary" truncate component="p">
        {preview}
      </Typography>
    )}
  </>
);

export const ConversationCard = ({
  actions,
  dateLabel,
  onClick,
  preview,
  title,
  variant = "card",
}: ConversationCardProps) => {
  if (variant === "list") {
    return (
      <div className="group flex rounded-lg transition-colors hover:bg-secondary">
        <button
          type="button"
          onClick={onClick}
          data-testid="circle-ai-project-chat-row"
          className="min-w-0 flex-1 p-4 text-left"
        >
          <div className="space-y-1">
            <ConversationSummary title={title} preview={preview} />
          </div>
          {dateLabel && (
            <div className="mt-3 flex items-center gap-2">
              <Icon name="clock-dash" size="sm" color="tertiary" />
              <Typography
                variant="caption"
                color="tertiary"
                component="p"
                data-testid="circle-ai-conversation-card-date"
              >
                {dateLabel}
              </Typography>
            </div>
          )}
        </button>
        {actions && (
          <div
            className={`shrink-0 self-start p-4 ${CARD_ACTIONS_REVEAL_CLASSES}`}
          >
            {actions}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group flex rounded-lg border border-secondary bg-primary shadow-2xs transition-colors hover:bg-secondary">
      <button
        type="button"
        onClick={onClick}
        className="min-w-0 flex-1 space-y-1 p-4 text-left"
      >
        {dateLabel && (
          <Typography variant="caption" color="disabled" component="p">
            {dateLabel}
          </Typography>
        )}
        <ConversationSummary title={title} preview={preview} />
      </button>
      {actions && (
        <div
          className={`shrink-0 self-start p-4 pl-0 ${CARD_ACTIONS_REVEAL_CLASSES}`}
        >
          {actions}
        </div>
      )}
    </div>
  );
};
